import React from "react";
import BookingHeader from "./booking/BookingHeader";
import { BookingLanguage, BookingStep } from "@/types/booking";

const DesktopSidebarSkeleton = () => (
  <div className="fixed top-0 right-0 h-screen w-1/4 bg-white border-l border-gray-200 shadow-xl flex flex-col z-50">
    {/* Header with title */}
    <div className="p-6">
      <div className="h-7 w-48 bg-gray-200 rounded animate-pulse" />
      <div className="h-4 w-full bg-gray-200 rounded mt-3 animate-pulse" />
    </div>

    {/* Scrollable content */}
    <div className="flex-1 overflow-y-auto p-6">
      <div className="space-y-4">
        <div className="border-2 border-gray-200 rounded-2xl p-6 space-y-4">
          <div className="h-5 w-full bg-gray-200 rounded animate-pulse" />
          <div className="h-5 w-full bg-gray-200 rounded animate-pulse" />
          <div className="h-5 w-full bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    </div>

    {/* Footer with total price, guarantee, and navigation */}
    <div className="p-6 border-t border-gray-200">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="h-7 w-24 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-28 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
      <div className="h-6 w-full bg-gray-200 rounded-full animate-pulse mb-4" />
      <div className="flex gap-3">
        <div className="h-10 flex-1 bg-gray-200 rounded-lg animate-pulse" />
        <div className="h-10 flex-1 bg-gray-300 rounded-lg animate-pulse" />
      </div>
    </div>
  </div>
);

// Skeleton for step 1 (product selection) - package cards grid
const ProductStepSkeleton = () => (
  <>
    <div className="text-center mb-10">
      <div className="h-7 lg:w-64 w-48 bg-gray-200 rounded mx-auto animate-pulse" />
      <div className="h-5 lg:w-96 w-64 bg-gray-200 rounded mx-auto mt-2 animate-pulse" />
    </div>

    {/* Package Type Toggle skeleton */}
    <div className="flex justify-center mb-10">
      <div
        className="bg-white rounded-full p-1 flex gap-1"
        style={{
          boxShadow: "0px 0px 30px 0px #2D2D2D0A, 0px 16px 60px 0px #2D2D2D1F",
        }}
      >
        <div className="h-8 w-24 bg-gray-200 rounded-full animate-pulse" />
        <div className="h-8 w-28 bg-gray-100 rounded-full animate-pulse" />
      </div>
    </div>

    <div className="grid lg:grid-cols-3 grid-cols-1 gap-5 container">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-[22px] h-full">
          <div
            className="bg-white rounded-[20px] p-3 pb-6 flex flex-col h-full"
            style={{ boxShadow: "0px 4px 40px rgba(45, 45, 45, 0.08)" }}
          >
            <div className="flex flex-col gap-6">
              {/* Top section */}
              <div className="flex flex-col gap-4">
                {/* Badge row + info button */}
                <div className="flex flex-row justify-between items-center h-[22px]">
                  <div className="h-[22px] w-20 bg-gray-200 rounded-full animate-pulse" />
                  <div className="w-[22px] h-[22px] bg-gray-200 rounded-full animate-pulse" />
                </div>

                <div className="flex flex-col gap-1 px-1">
                  {/* Package name */}
                  <div className="h-9 w-3/4 bg-gray-200 rounded animate-pulse" />

                  {/* Duration info */}
                  <div className="h-4 w-2/3 bg-gray-200 rounded animate-pulse" />

                  {/* Description - 80px height */}
                  <div className="h-[80px] mt-2 space-y-2">
                    <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-5/6 bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-2/3 bg-gray-200 rounded animate-pulse" />
                  </div>

                  {/* Price */}
                  <div className="h-[29px] w-32 bg-gray-200 rounded animate-pulse" />
                </div>

                {/* CTA Button */}
                <div
                  className={`h-[40px] w-full rounded-lg animate-pulse ${
                    i === 2
                      ? "bg-aubergine-800/20"
                      : i === 0
                        ? "bg-aubergine-600/20"
                        : "bg-deep-blue-950/20"
                  }`}
                />
              </div>

              {/* Feature icons row */}
              <div className="flex flex-row items-start gap-2 px-1">
                {[0, 1, 2].map((j) => (
                  <div
                    key={j}
                    className="flex flex-col items-center gap-1 py-2 px-1 flex-1"
                  >
                    <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse" />
                    <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 w-3/4 bg-gray-200 rounded animate-pulse" />
                  </div>
                ))}
              </div>

              {/* Checklist items */}
              <div className="flex flex-col px-1">
                {[0, 1, 2, 3, 4].map((j) => (
                  <div
                    key={j}
                    className="h-[40px] flex items-center px-2 gap-2"
                  >
                    <div className="w-4 h-4 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
                    <div className="h-4 flex-1 bg-gray-200 rounded animate-pulse" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </>
);

// Skeleton for step 2+ (addons, clinic, date, etc.) - list of cards
const AddOnsStepSkeleton = () => (
  <>
    <div className="text-center mb-10">
      <div className="h-7 lg:w-64 w-48 bg-gray-200 rounded mx-auto animate-pulse" />
      <div className="h-5 lg:w-96 w-64 bg-gray-200 rounded mx-auto mt-2 animate-pulse" />
    </div>

    <div className="space-y-3 pb-12">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="rounded-[22px]">
          <div
            className="bg-white rounded-[20px] p-4"
            style={{
              boxShadow:
                "0px 0px 30px 0px #2D2D2D0A, 0px 16px 60px 0px #2D2D2D1F",
            }}
          >
            <div className="flex items-center gap-4">
              {/* Product Image */}
              <div className="w-16 h-16 bg-gray-200 rounded-xl flex-shrink-0 animate-pulse" />

              {/* Product Info */}
              <div className="flex-grow flex flex-col gap-2">
                <div className="h-5 w-3/4 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
              </div>

              {/* Selection Indicator */}
              <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </>
);

interface BookingSkeletonProps {
  language?: BookingLanguage;
  step?: BookingStep;
}

const BookingSkeleton: React.FC<BookingSkeletonProps> = ({
  language = "en-US" as BookingLanguage,
  step = "product",
}) => {
  const isStep1 = step === "product";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className={isStep1 ? "" : "xl:pr-[25%]"}>
        <BookingHeader
          language={language}
          skeletonMode={true}
          currentStep={step}
          setLanguage={() => {}} // Dummy function for skeleton state
        />

        <main className="flex-1 px-4 py-8 container mx-auto">
          {isStep1 ? <ProductStepSkeleton /> : <AddOnsStepSkeleton />}
        </main>
      </div>

      {/* Mobile footer skeleton - only for step 2+ */}
      {!isStep1 && (
        <div className="w-full sticky bottom-0 bg-white border-t border-gray-200 shadow-lg xl:hidden">
          <div className="max-w-5xl mx-auto px-4 py-3 h-20">
            <div className="flex items-center justify-between gap-3">
              <div className="h-10 flex-1 bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-10 flex-1 bg-gray-300 rounded-lg animate-pulse" />
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar skeleton - only for step 2+ */}
      {!isStep1 && (
        <div className="hidden xl:block">
          <DesktopSidebarSkeleton />
        </div>
      )}
    </div>
  );
};

export default BookingSkeleton;
